import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import axios from "axios";
import { getAuthHttpHeaders } from "../auth";
import Dimensions from "react-dimensions";
import {
  VictoryLine,
  VictoryChart,
  VictoryScatter,
  VictoryZoomContainer,
  VictoryTheme,
  VictoryAxis,
  VictoryLabel,
  VictoryPortal,
} from "victory";
import { instantFromApi } from "../utils/dateTime";
import { Message, Button } from "semantic-ui-react";
import { getBotConfig, getBotState } from "../utils/botUtils";
import PercentagePieChart from "../charts/PercentagePieChart";
import MonthProfitChart from "../charts/MonthProfitChart";

@inject("store")
@observer
class SimStats extends Component {
  state = {
    simLoading: false,
    botLoading: false,
    simData: null,
    botData: null,
    openDate: true,
  };

  componentDidMount() {
    this.load(this.props.sim);
  }

  componentWillReceiveProps(newProps) {
    if (newProps.sim !== this.props.sim) {
      this.load(newProps.sim);
    }
  }

  load(sim) {
    this.loadPart(sim.id, "sim");
    this.loadPart(sim.botId, "bot");
  }

  /**
   * @param what sim | bot
   */
  async loadPart(id, what) {
    if (!id) {
      this.setState({});
      this.setState({
        [what + "Loading"]: false,
        [what + "Data"]: null,
      });
      return;
    }

    this.setState({ [what + "Loading"]: true });

    try {
      let axiosRequest = axios.create({
        baseURL: "api",
        timeout: 30 * 1000,
        headers: {
          Accept: "application/json",
          ...getAuthHttpHeaders(),
        },
        params: {
          openDate: this.state.openDate,
        },
      });

      let response = await axiosRequest.get(`chart/profit/${what}/${id}`);
      this.setState({ [what + "Data"]: response.data });
    } catch (e) {
      this.setState({ [what + "Data"]: null });
    } finally {
      this.setState({ [what + "Loading"]: false });
    }
  }

  render() {
    const {
      store: {
        chartsDisplayed: { closedProfitCumulative, closedProfit },
        toggleChartDisplayed,
      },
      sim,
    } = this.props;

    const state = getBotState(sim);
    const config = getBotConfig(sim);

    return (
      <div>
        <Button
          content="Open Date"
          toggle
          active={this.state.openDate}
          onClick={() => {
            this.setState({ openDate: !this.state.openDate }, () => this.load(sim));
          }}
        />
        <Button
          content="Cumulative Closed Profit"
          toggle
          active={closedProfitCumulative}
          onClick={() => toggleChartDisplayed("closedProfitCumulative")}
        />
        <Button
          content="Closed Trade Profit"
          toggle
          active={closedProfit}
          onClick={() => toggleChartDisplayed("closedProfit")}
        />

        <Button content="Refresh" icon="refresh" onClick={() => this.load(sim)} loading={this.state.loading} />

        {!this.state.simLoading && !this.state.simData && <Message negative>No data.</Message>}

        {this.state.simData &&
          (closedProfitCumulative || closedProfit) && (
            <VictoryChart
              scale={{ x: "time", y: "linear" }}
              width={this.props.containerWidth}
              height={400}
              theme={VictoryTheme.material}
              key={sim.id + ":" + this.state.openDate + "," + closedProfitCumulative + "," + closedProfit} // fix y-axis with VictoryZoomContainer when toggling charts
              containerComponent={<VictoryZoomContainer zoomDimension="x" />}
            >
              <VictoryAxis />
              <VictoryAxis
                dependentAxis
                tickLabelComponent={
                  <VictoryPortal>
                    <VictoryLabel
                      text={d => {
                        const percents = (d / config.tradeTotal * 100).toFixed(1) + "%";
                        return d + "\n" + percents;
                      }}
                    />
                  </VictoryPortal>
                }
              />
              {closedProfitCumulative && (
                <VictoryLine
                  data={this.state.simData}
                  x={d => instantFromApi(d.date).toEpochMilli()}
                  y="cumulativeProfit"
                  interpolation="stepAfter"
                  style={{
                    data: {
                      strokeOpacity: 0.8,
                    },
                  }}
                />
              )}

              {closedProfitCumulative &&
                this.state.botData && (
                  <VictoryLine
                    data={this.state.botData}
                    x={d => instantFromApi(d.date).toEpochMilli()}
                    y="cumulativeProfit"
                    interpolation="stepAfter"
                    style={{
                      data: {
                        stroke: "#800080",
                        strokeOpacity: 0.8,
                      },
                    }}
                  />
                )}

              {closedProfit && (
                <VictoryScatter
                  data={this.state.simData}
                  x={d => instantFromApi(d.date).toEpochMilli()}
                  y="profit"
                  size={4}
                  style={{
                    data: {
                      fill: d => (d.profit > 0 ? "green" : "red"),
                      fillOpacity: 0.6,
                    },
                  }}
                />
              )}

              {closedProfit &&
                this.state.botData && (
                  <VictoryScatter
                    data={this.state.botData}
                    x={d => instantFromApi(d.date).toEpochMilli()}
                    y="profit"
                    size={4}
                    style={{
                      data: {
                        fill: d => (d.profit > 0 ? "#800080" : "#800080"),
                        fillOpacity: 0.6,
                      },
                    }}
                  />
                )}
            </VictoryChart>
          )}
        <MonthProfitChart
          trades={this.state.simData}
          tradeTotal={config.tradeTotal}
          width={this.props.containerWidth}
        />
        <div>
          {(state.stats &&
            state.stats.openRealized && (
              <PercentagePieChart
                key={sim.id + "perc1"}
                style={{ width: "300px", display: "inline-block" }}
                value={state.stats.openRealized / state.stats.openAttempts * 100}
                label="Avg. opened trades"
              />
            )) ||
            ""}
          {(state.stats &&
            state.stats.openRealized && (
              <PercentagePieChart
                key={sim.id + "perc2"}
                style={{ width: "300px", display: "inline-block" }}
                value={state.stats.avgRealizedAmountPct}
                label="Avg. realized amount"
              />
            )) ||
            ""}
        </div>
      </div>
    );
  }
}

export default Dimensions()(SimStats);



// WEBPACK FOOTER //
// ./src/sim/SimStats.js